import React, {
  forwardRef,
  useCallback,
  useContext,
  useImperativeHandle,
  useMemo,
} from 'react';
import classNames from 'classnames';
import { Button, Modal } from 'ncoded-component-library';
import { OverlayRef } from 'ncoded-component-library/build/components/atoms/Overlay/Overlay.component';
import useCallbackRef from 'hooks/useCallbackRef';
import { Field, Form } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { NDA_COPIES, NDA_STATUSES } from 'models/Nda';
import { Lead } from 'models/Lead';
import confirm from 'modules/confirm';
import api from 'api';
import SelectField from 'components/SelectField';
import validations from 'validations';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import LeadsContext from '../../../../providers/Leads';
import DropzoneField from 'components/DropzoneField';
import { ACCEPTED_FILE_TYPES } from 'router/subrouters/DataRoom/components/DataRoomDocumentsRouter/data/DataTypes';
import { getUserFullName } from 'utils';
import TransWithNda from 'components/TransWithNda';

import './OverrideNdaStatusModal.styles.scss';

type OverrideNdaStatusModalProps = {
  className?: string;
  lead: Lead;
};

const OverrideNdaStatusModal: React.ForwardRefRenderFunction<
  OverlayRef,
  OverrideNdaStatusModalProps
> = (props, ref) => {
  const { className, lead } = props;

  const {
    _id,
    firstName,
    lastName,
    signingStatus,
    ndaFile,
    envelopeTemplateName,
  } = lead || {};

  const classes = classNames('override-nda-status-modal', className);

  const { popServerError } = useContext(PopNotificationsContext);

  const { updateLead } = useContext(LeadsContext);

  const [modal, modalRef] = useCallbackRef<OverlayRef>();
  useImperativeHandle(ref, () => modal, [modal]);

  const { t } = useTranslation();

  const selectOptions = useMemo(
    () =>
      Object.values(NDA_STATUSES).map((value) => ({
        label: (
          <TransWithNda
            i18nKey={NDA_COPIES[value]}
            envelopeTemplateName={envelopeTemplateName}
          />
        ),
        value,
      })),
    [envelopeTemplateName],
  );

  const uploadNDAProof = useCallback(
    async (file, _, config) => {
      const data = await api.portfolio.seller.uploadNDAProof(file, _id, config);

      return { ...data, name: file.name };
    },
    [_id],
  );

  const deletedNDAProof = useCallback(
    async (file) => {
      if (file._id && file._id === ndaFile?._id) {
        await api.portfolio.seller.removeNdaProofFile(_id, ndaFile._id);

        updateLead({ ...lead, ndaFile: undefined });
      }

      return;
    },
    [_id, lead, ndaFile, updateLead],
  );

  const onSubmit = useCallback(
    async ({ status, uploadedFile }) => {
      if (
        status !== NDA_STATUSES.SIGNED ||
        (await confirm({
          title: t('ndaChangeToSigned'),
          content: (
            <TransWithNda
              i18nKey="ndaChangeToSignedMessage"
              envelopeTemplateName={envelopeTemplateName}
            />
          ),
          cancelBtnText: t('goBack'),
          submitBtnText: t('submit'),
        }))
      ) {
        try {
          const { data } = await api.portfolio.seller.setNdaManually(
            _id,
            status,
            uploadedFile,
          );

          updateLead(data);
          modal.close();
        } catch (e) {
          popServerError(e);
        }
      }
    },
    [_id, modal, envelopeTemplateName, popServerError, t, updateLead],
  );

  return (
    <Modal
      ref={modalRef}
      className={classes}
      title={
        <TransWithNda
          i18nKey="updateNdaStatus"
          values={{ name: getUserFullName(firstName, lastName) }}
          envelopeTemplateName={envelopeTemplateName}
        />
      }
    >
      <TransWithNda
        i18nKey="reminderThatUpdatingNDA"
        envelopeTemplateName={envelopeTemplateName}
      />

      <Form
        initialValues={{ status: signingStatus, uploadedFile: ndaFile }}
        onSubmit={onSubmit}
        render={({ invalid, pristine, submitting, handleSubmit }) => (
          <form onSubmit={handleSubmit}>
            <Field
              label={t('status')}
              name="status"
              component={SelectField}
              options={selectOptions}
              validate={validations.required()}
            />

            <Field
              accept={ACCEPTED_FILE_TYPES}
              component={DropzoneField}
              maxFiles={1}
              showApplicationAsIframe
              name="uploadedFile"
              label={t('ndaStatusProof')}
              uploadRequest={uploadNDAProof}
              deleteRequest={deletedNDAProof}
            />

            <div className="actions">
              <Button variant="link" onClick={modal?.close}>
                {t('cancel')}
              </Button>
              <Button
                type="submit"
                disabled={invalid || pristine || submitting}
              >
                {t('confirmStatus')}
              </Button>
            </div>
          </form>
        )}
      />
    </Modal>
  );
};

export default forwardRef(OverrideNdaStatusModal);
